import React, { useState } from 'react';
import { BarChart3, TrendingUp, Users, ChevronDown, ChevronUp, Award, Percent } from 'lucide-react';
import electionResultsData from '../data/election_results.json';

function ElectionResults() {
  const [selectedElection, setSelectedElection] = useState(null);
  const [expandedBooth, setExpandedBooth] = useState(null);
  
  // update the code
  const electionResults = electionResultsData;
  // party colors
  const partyColors = {
    "தி.மு.க": "bg-red-500",
    "அ.தி.மு.க": "bg-green-600",
    "பா.ஜ.க": "bg-orange-500",
    "நா.த.க": "bg-rose-500",
    "த.வெ.க": "bg-yellow-400",
    "மற்றவை": "bg-gray-500"
  };

  const calculatePercentage = (votes, total) => {
    if (!total || total === 0) return "0.00";
    return ((parseInt(votes) / parseInt(total)) * 100).toFixed(2);
  };

  const calculateTurnout = (polled, total) => {
    if (!total || total === 0) return "0.00";
    return ((parseInt(polled) / parseInt(total)) * 100).toFixed(2);
  };

  // This function is kept but commented out since it's not currently used
  // If you need it later, you can uncomment it
  /*
  const getWinningParty = (partyResults) => {
    if (!partyResults) return { party: 'N/A', votes: 0 };
    
    let maxVotes = 0;
    let winner = '';
    
    Object.entries(partyResults).forEach(([party, votes]) => {
      const voteCount = parseInt(votes) || 0;
      if (voteCount > maxVotes) {
        maxVotes = voteCount;
        winner = party;
      }
    });
    
    return { party: winner || 'N/A', votes: maxVotes };
  };
  */

  const calculateElectionSummary = (boothResults) => {
    if (!boothResults || !Array.isArray(boothResults)) {
      return { summary: {}, totalVotes: 0, totalPolled: 0 };
    }

    const summary = {};
    let totalVotes = 0;
    let totalPolled = 0;

    boothResults.forEach(booth => {
      if (!booth) return;
      
      totalVotes += parseInt(booth.total_vote) || 0;
      totalPolled += parseInt(booth.vote_polled) || 0;
      
      if (booth.party_results && typeof booth.party_results === 'object') {
        Object.entries(booth.party_results).forEach(([party, votes]) => {
          const voteCount = parseInt(votes) || 0;
          summary[party] = (summary[party] || 0) + voteCount;
        });
      }
    });

    return { summary, totalVotes, totalPolled };
  };

  // Function to sort booth results by booth_number
  const sortBoothsByNumber = (boothResults) => {
    if (!boothResults || !Array.isArray(boothResults)) return [];
    return [...boothResults].sort((a, b) => {
      const numA = parseInt(a.booth_number) || 0;
      const numB = parseInt(b.booth_number) || 0;
      return numA - numB;
    });
  };

  return (
    <div>
      {/* Page Header */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <BarChart3 className="w-10 h-10 mr-4" />
            <h1 className="text-4xl font-bold">தேர்தல் முடிவுகள்</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Election Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {electionResults.map((election) => {
            const { totalVotes, totalPolled } = calculateElectionSummary(election.booth_results);
            // Removed unused summary variable
            const isSelected = selectedElection?.id === election.id;

            return (
              <div
                key={election.id}
                onClick={() => setSelectedElection(isSelected ? null : election)}
                className={`cursor-pointer bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 ${
                  isSelected ? 'ring-4 ring-orange-500 transform scale-105' : ''
                }`}
              >
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <Award className="w-8 h-8" />
                    <span className="text-sm text-black bg-white bg-opacity-20 px-3 py-1 rounded-full">
                      {election.booth_results?.length || 0} பூதஂகளஂ
                    </span>
                  </div>
                  <h3 className="text-xl font-bold">{election.election}</h3>
                </div>

                <div className="p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">மொத்த வாக்குகள்:</span>
                      <span className="font-bold text-lg">{totalVotes.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">பதிவான வாக்குகள்:</span>
                      <span className="font-bold text-lg text-green-600">{totalPolled.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">வாக்குப்பதிவு %:</span>
                      <span className="font-bold text-lg text-blue-600">
                        {calculateTurnout(totalPolled, totalVotes)}%
                      </span>
                    </div>
                  </div>

                  <button className="w-full mt-4 bg-orange-500 text-white py-2 rounded-lg font-semibold hover:bg-orange-600 transition-colors">
                    {isSelected ? 'மூடு' : 'விபரம் பார்க்க'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detailed Results */}
        {selectedElection && (
          <div className="bg-white rounded-xl shadow-lg p-2 mb-8 animate-fadeIn">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-orange-500" />
              {selectedElection.election} - விரிவான முடிவுகள்
            </h2>

            {/* Overall Summary */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">மொத்த முடிவுகள்</h3>
              {(() => {
                const { summary, totalPolled } = calculateElectionSummary(selectedElection.booth_results);
                const sortedParties = Object.entries(summary).sort((a, b) => b[1] - a[1]);

                return (
                  <div className="space-y-3">
                    {sortedParties.map(([party, votes], index) => (
                      <div key={party} className="bg-white rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            {index === 0 && <Award className="w-5 h-5 text-yellow-500" />}
                            <span className="font-semibold text-gray-800">{party}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-lg">{votes.toLocaleString()}</div>
                            <div className="text-sm text-gray-600">
                              {calculatePercentage(votes, totalPolled)}%
                            </div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className={`h-3 rounded-full transition-all duration-500 ${partyColors[party] || 'bg-gray-500'}`}
                            style={{ width: `${calculatePercentage(votes, totalPolled)}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Booth-wise Results */}
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-500" />
              பூத் வாரியான முடிவுகள்
            </h3>

            <div className="space-y-4">
              {sortBoothsByNumber(selectedElection.booth_results).map((booth) => {
                const isExpanded = expandedBooth === booth.id;
                // Removed unused winner variable
                const turnout = calculateTurnout(booth.vote_polled, booth.total_vote);

                return (
                  <div key={booth.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      onClick={() => setExpandedBooth(isExpanded ? null : booth.id)}
                      className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                              பூத் {booth.booth_number}
                            </span>
                            <h4 className="font-bold text-gray-800">{booth.booth_name}</h4>
                          </div>
                          <div className="flex gap-6 text-sm text-gray-600">
                            <span>மொத்தம்: <strong>{booth.total_vote}</strong></span>
                            <span>பதிவு: <strong>{booth.vote_polled}</strong></span>
                            <span className="text-blue-600">
                              <Percent className="w-4 h-4 inline mr-1" />
                              <strong>{turnout}%</strong>
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-4 bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {booth.party_results && Object.entries(booth.party_results)
                            .sort((a, b) => (parseInt(b[1]) || 0) - (parseInt(a[1]) || 0))
                            .map(([party, votes]) => (
                              <div key={party} className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold text-gray-800">{party}</span>
                                  <span className="font-bold text-lg">{votes}</span>
                                </div>
                                <div className="text-sm text-gray-600 mb-2">
                                  {calculatePercentage(votes, booth.vote_polled)}% of polled votes
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div
                                    className={`h-2 rounded-full transition-all duration-500 ${partyColors[party] || 'bg-gray-500'}`}
                                    style={{ width: `${calculatePercentage(votes, booth.vote_polled)}%` }}
                                  ></div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold text-gray-700 mb-3">கட்சி வண்ண குறியீடு</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(partyColors).map(([party, color]) => (
              <div key={party} className="flex items-center gap-2">
                <div className={`w-4 h-4 ${color} rounded`}></div>
                <span className="text-sm text-gray-600">{party}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}

export default ElectionResults;